import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import api from '@/utils/api';
import { toast } from 'sonner';
import { Gamepad2, TrendingUp, Trophy, Coins, ArrowLeft, RotateCcw, Flame, Target } from 'lucide-react';
import { MobileBottomNav } from '@/components/MobileNav';

const SimulatorPage = () => {
  const navigate = useNavigate();
  const [portfolio, setPortfolio] = useState(null);
  const [bets, setBets] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  const [matchDesc, setMatchDesc] = useState('');
  const [prediction, setPrediction] = useState('');
  const [odds, setOdds] = useState('');
  const [stake, setStake] = useState('');
  const [sport, setSport] = useState('Soccer');
  const [placing, setPlacing] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [p, h, l] = await Promise.all([
        api.get('/simulator/portfolio'),
        api.get('/simulator/history?limit=20'),
        api.get('/simulator/leaderboard'),
      ]);
      setPortfolio(p.data);
      setBets(h.data.bets || []);
      setLeaderboard(l.data.leaderboard || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const placeBet = async (e) => {
    e.preventDefault();
    if (!matchDesc || !prediction || !odds || !stake) {
      toast.error('Bitte alle Felder ausfuellen');
      return;
    }
    setPlacing(true);
    try {
      await api.post('/simulator/bet', {
        match_description: matchDesc,
        prediction,
        odds: parseFloat(odds),
        stake: parseFloat(stake),
        sport,
      });
      toast.success('Wette platziert!');
      setMatchDesc('');
      setPrediction('');
      setOdds('');
      setStake('');
      loadData();
    } catch (err) {
      toast.error(err.message || 'Fehler beim Platzieren');
    } finally {
      setPlacing(false);
    }
  };

  const settleBet = async (betId, won) => {
    try {
      await api.post('/simulator/settle', { bet_id: betId, won });
      toast.success(won ? 'Gewonnen!' : 'Verloren');
      loadData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const resetPortfolio = async () => {
    if (!window.confirm('Portfolio wirklich zuruecksetzen? Alle Wetten werden geloescht.')) return;
    try {
      await api.post('/simulator/reset');
      toast.success('Portfolio zurueckgesetzt');
      loadData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#121212] flex items-center justify-center">
        <div className="animate-pulse text-[#00BFA6]">Simulator wird geladen...</div>
      </div>
    );
  }

  const winRate = portfolio?.total_bets > 0 ? ((portfolio.won_bets / portfolio.total_bets) * 100).toFixed(1) : 0;

  return (
    <div className="min-h-screen bg-[#121212] pb-24">
      <header className="bg-[#1E1E1E] border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/dashboard')} className="text-gray-400 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <Gamepad2 className="w-5 h-5 text-[#00BFA6]" />
              <h1 className="text-xl font-bold text-white" data-testid="simulator-title">Wett-Simulator</h1>
            </div>
          </div>
          <Button onClick={resetPortfolio} variant="ghost" className="text-gray-400 hover:text-red-400 text-xs" data-testid="reset-portfolio-btn">
            <RotateCcw className="w-4 h-4 mr-1" /> Reset
          </Button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Portfolio Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="bg-[#1E1E1E] border-white/10">
            <CardContent className="pt-4 pb-4">
              <div className="text-xs text-gray-400 mb-1">Guthaben</div>
              <div className="text-2xl font-bold text-[#00BFA6] font-mono" data-testid="sim-balance">{(portfolio?.balance || 0).toLocaleString()}</div>
              <div className="text-[10px] text-gray-500">Coins</div>
            </CardContent>
          </Card>
          <Card className="bg-[#1E1E1E] border-white/10">
            <CardContent className="pt-4 pb-4">
              <div className="text-xs text-gray-400 mb-1">Wetten</div>
              <div className="text-2xl font-bold text-white font-mono">{portfolio?.total_bets || 0}</div>
              <div className="text-[10px] text-gray-500">{portfolio?.pending_bets || 0} offen</div>
            </CardContent>
          </Card>
          <Card className="bg-[#1E1E1E] border-white/10">
            <CardContent className="pt-4 pb-4">
              <div className="text-xs text-gray-400 mb-1">Trefferquote</div>
              <div className="text-2xl font-bold text-white font-mono">{winRate}%</div>
              <div className="text-[10px] text-gray-500">{portfolio?.won_bets || 0}W / {portfolio?.lost_bets || 0}L</div>
            </CardContent>
          </Card>
          <Card className="bg-[#1E1E1E] border-white/10">
            <CardContent className="pt-4 pb-4">
              <div className="text-xs text-gray-400 mb-1">Profit</div>
              <div className={`text-2xl font-bold font-mono ${(portfolio?.total_profit || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {(portfolio?.total_profit || 0) >= 0 ? '+' : ''}{(portfolio?.total_profit || 0).toLocaleString()}
              </div>
              <div className="text-[10px] text-gray-500 flex items-center gap-1">
                <Flame className="w-3 h-3 text-orange-400" /> Streak: {portfolio?.current_streak || 0}
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="place" className="w-full">
          <TabsList className="bg-[#1E1E1E] border border-white/10">
            <TabsTrigger value="place" data-testid="tab-place-bet">Wette platzieren</TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-bet-history">Verlauf</TabsTrigger>
            <TabsTrigger value="ranking" data-testid="tab-sim-ranking">Rangliste</TabsTrigger>
          </TabsList>

          <TabsContent value="place" className="mt-4">
            <Card className="bg-[#1E1E1E] border-white/10">
              <CardHeader>
                <CardTitle className="text-white text-lg">Neue virtuelle Wette</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={placeBet} className="space-y-4">
                  <div>
                    <label className="text-sm text-gray-400 block mb-1">Spiel</label>
                    <input
                      data-testid="sim-match-input"
                      type="text"
                      value={matchDesc}
                      onChange={(e) => setMatchDesc(e.target.value)}
                      placeholder="z.B. Bayern vs Dortmund"
                      className="w-full bg-[#121212] border border-white/10 rounded-lg px-4 py-3 text-white outline-none focus:border-[#00BFA6]"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm text-gray-400 block mb-1">Vorhersage</label>
                      <input
                        data-testid="sim-prediction-input"
                        type="text"
                        value={prediction}
                        onChange={(e) => setPrediction(e.target.value)}
                        placeholder="Sieg Heim / Over 2.5"
                        className="w-full bg-[#121212] border border-white/10 rounded-lg px-4 py-3 text-white outline-none focus:border-[#00BFA6]"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-gray-400 block mb-1">Sportart</label>
                      <select
                        data-testid="sim-sport-select"
                        value={sport}
                        onChange={(e) => setSport(e.target.value)}
                        className="w-full bg-[#121212] border border-white/10 rounded-lg px-4 py-3 text-white outline-none focus:border-[#00BFA6]"
                      >
                        <option value="Soccer">Fussball</option>
                        <option value="Basketball">Basketball</option>
                        <option value="Tennis">Tennis</option>
                        <option value="Ice Hockey">Eishockey</option>
                        <option value="American Football">American Football</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm text-gray-400 block mb-1">Quote</label>
                      <input
                        data-testid="sim-odds-input"
                        type="number"
                        step="0.01"
                        min="1.01"
                        value={odds}
                        onChange={(e) => setOdds(e.target.value)}
                        placeholder="2.10"
                        className="w-full bg-[#121212] border border-white/10 rounded-lg px-4 py-3 text-white outline-none focus:border-[#00BFA6]"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-gray-400 block mb-1">Einsatz (Coins)</label>
                      <input
                        data-testid="sim-stake-input"
                        type="number"
                        min="1"
                        max="5000"
                        value={stake}
                        onChange={(e) => setStake(e.target.value)}
                        placeholder="100"
                        className="w-full bg-[#121212] border border-white/10 rounded-lg px-4 py-3 text-white outline-none focus:border-[#00BFA6]"
                      />
                    </div>
                  </div>
                  {odds && stake && (
                    <div className="bg-[#121212] rounded-lg p-3 text-sm">
                      <span className="text-gray-400">Moeglicher Gewinn: </span>
                      <span className="text-[#00BFA6] font-bold font-mono">{(parseFloat(odds || 0) * parseFloat(stake || 0)).toFixed(2)} Coins</span>
                    </div>
                  )}
                  <Button
                    data-testid="sim-place-bet-btn"
                    type="submit"
                    disabled={placing}
                    className="w-full bg-[#00BFA6] hover:bg-[#009E8A] text-black font-bold py-3"
                  >
                    {placing ? 'Wird platziert...' : 'Wette platzieren'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="history" className="mt-4">
            <Card className="bg-[#1E1E1E] border-white/10">
              <CardContent className="pt-6">
                {bets.length === 0 ? (
                  <div className="text-gray-500 text-center py-8">Noch keine Wetten platziert</div>
                ) : (
                  <div className="space-y-3">
                    {bets.map((bet, i) => (
                      <div key={bet.bet_id || i} data-testid={`sim-bet-${i}`} className="p-4 bg-[#121212] rounded-lg border border-white/5">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <div className="text-white font-medium">{bet.match_description}</div>
                            <div className="text-xs text-gray-500 mt-0.5">{bet.prediction} — {bet.sport}</div>
                          </div>
                          <div className="text-right">
                            <div className="text-white font-mono">{bet.odds}x</div>
                            <div className="text-xs text-gray-500">{bet.stake} Coins</div>
                          </div>
                        </div>
                        {bet.status === 'pending' ? (
                          <div className="flex gap-2 mt-2">
                            <Button
                              data-testid={`settle-won-${i}`}
                              onClick={() => settleBet(bet.bet_id, true)}
                              className="bg-green-600 hover:bg-green-700 text-white text-xs py-1 h-auto flex-1"
                            >
                              Gewonnen
                            </Button>
                            <Button
                              data-testid={`settle-lost-${i}`}
                              onClick={() => settleBet(bet.bet_id, false)}
                              className="bg-red-600 hover:bg-red-700 text-white text-xs py-1 h-auto flex-1"
                            >
                              Verloren
                            </Button>
                          </div>
                        ) : (
                          <div className={`text-xs mt-2 px-2 py-1 rounded inline-block ${
                            bet.status === 'won' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                          }`}>
                            {bet.status === 'won' ? `Gewonnen: +${bet.payout} Coins` : `Verloren: -${bet.stake} Coins`}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="ranking" className="mt-4">
            <Card className="bg-[#1E1E1E] border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-yellow-400" /> Top Simulanten
                </CardTitle>
              </CardHeader>
              <CardContent>
                {leaderboard.length === 0 ? (
                  <div className="text-gray-500 text-center py-8">Noch keine Rangliste (min. 5 Wetten)</div>
                ) : (
                  <div className="space-y-2">
                    {leaderboard.map((l, i) => (
                      <div key={i} data-testid={`sim-leader-${i}`} className="flex items-center justify-between p-3 bg-[#121212] rounded-lg">
                        <div className="flex items-center gap-3">
                          <span className={`text-lg font-bold ${i === 0 ? 'text-yellow-400' : i === 1 ? 'text-gray-300' : i === 2 ? 'text-orange-400' : 'text-gray-500'}`}>
                            #{l.rank}
                          </span>
                          <div>
                            <div className="text-white font-medium">{l.anon_name}</div>
                            <div className="text-xs text-gray-500">{l.total_bets} Wetten — {l.win_rate}%</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-[#00BFA6] font-bold font-mono">{Math.round(l.balance).toLocaleString()}</div>
                          <div className="text-xs text-gray-500">Coins</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      <MobileBottomNav />
    </div>
  );
};

export default SimulatorPage;
